import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BadgeCheck, BarChart3, Gamepad2, KeyRound, Layers, ListMusic, LogOut, Menu, MessageSquarePlus, Puzzle, ScrollText, Trophy, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { NotificationBell } from "@/components/teacher/Social";
import { FeedbackHost, openFeedback } from "@/components/teacher/SiteAdmin";
import { SiteFooter } from "@/components/Legal";

const FeedbackButton = ({ id }) => (
  <Button variant="ghost" onClick={openFeedback} className="justify-start text-slate-300" data-testid={id}><MessageSquarePlus className="h-4 w-4 mr-2" />Geri bildirim gönder</Button>
);

const NAV = [
  { to: "/ogretmen/havuzlar", label: "Soru Havuzları", icon: Layers, id: "pools" },
  { to: "/ogretmen/oyunlar", label: "Oyunlar", icon: Gamepad2, id: "games" },
  { to: "/ogretmen/etkinlikler", label: "Etkinlikler", icon: Puzzle, id: "activities" },
  { to: "/ogretmen/listeler", label: "Oyun Listeleri", icon: ListMusic, id: "playlists" },
  { to: "/ogretmen/analiz", label: "Analiz", icon: BarChart3, id: "analytics" },
  { to: "/ogretmen/rekorlar", label: "Sınıf Rekorları", icon: Trophy, id: "records" },
];
const REVIEW_NAV = { to: "/ogretmen/onaylar", label: "Onaylar", icon: BadgeCheck, id: "reviews" };
const ADMIN_NAV = { to: "/ogretmen/kodlar", label: "Yönetim", icon: KeyRound, id: "codes" };
const DEV_NAV = { to: "/ogretmen/loglar", label: "Geliştirici", icon: ScrollText, id: "logs" };

const NavItem = ({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.id}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2 rounded-2xl font-semibold transition-[background-color,color,box-shadow] duration-200 ${
          isActive ? "bg-[#FACC15] text-[#1A1405] shadow-[0_4px_0_#A16207]" : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <Icon className="h-5 w-5" />
      {item.label}
    </NavLink>
  );
};

export default function PanelLayout() {
  const { teacher, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const items = [...NAV, ...(teacher?.role === "admin" || teacher?.academic || teacher?.developer ? [REVIEW_NAV] : []), ...(teacher?.role === "admin" ? [ADMIN_NAV] : []), ...(teacher?.developer ? [DEV_NAV] : [])];
  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <header className="md:hidden sticky top-0 z-40 flex items-center gap-3 border-b border-white/10 bg-[#161C27]/90 backdrop-blur-xl px-4 py-3" data-testid="mobile-header">
        <Logo />
        <div className="ml-auto flex items-center gap-2">
          <NotificationBell />
          <button onClick={() => setOpen((o) => !o)} className="p-2.5 rounded-xl border border-white/10" aria-label="Menü" data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>
      {open && (
        <div className="md:hidden fixed inset-0 top-[65px] z-30 bg-[#0B0F16]/95 backdrop-blur-xl p-4 flex flex-col gap-1 overflow-y-auto anim-fade-up" data-testid="mobile-menu">
          {items.map((item) => <NavItem key={item.id} item={item} onClick={() => setOpen(false)} />)}
          <p className="text-xs text-slate-400 mt-4 px-4">{teacher?.email}</p>
          <FeedbackButton id="mobile-feedback-button" />
          <Button variant="outline" onClick={logout} className="mt-2" data-testid="mobile-logout-button"><LogOut className="h-4 w-4 mr-2" /> Çıkış</Button>
        </div>
      )}
      <aside className="hidden md:flex md:w-72 md:min-h-screen md:m-4 md:mr-0 md:rounded-[1.75rem] border border-white/10 bg-[#161C27]/80 backdrop-blur-xl p-4 flex-col gap-2 sticky top-4 h-[calc(100vh-2rem)] overflow-hidden">
        <div className="flex items-center gap-2"><Logo /><div className="ml-auto"><NotificationBell /></div></div>
        <p className="eyebrow mt-2">Öğretmen Paneli</p>
        <nav className="flex flex-col gap-0.5 min-h-0">
          {items.map((item) => <NavItem key={item.id} item={item} />)}
        </nav>
        <div className="mt-auto flex flex-col gap-1.5">
          <p className="text-xs text-slate-400 truncate" data-testid="teacher-email">{teacher?.email}</p>
          <FeedbackButton id="feedback-button" />
          <Button variant="outline" onClick={logout} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" /> Çıkış</Button>
        </div>
      </aside>
      <main className="flex-1 p-4 sm:p-8 lg:p-12 min-w-0 overflow-x-hidden">
        <Outlet />
        <SiteFooter inPanel />
        <FeedbackHost />
      </main>
    </div>
  );
}
